import { useTranslation } from 'react-i18next';
import { SEOHead } from './SEOHead';
import { NavigationLink } from './Router';

export function FoodPoisoningReportPage() {
  const { i18n } = useTranslation();
  const isNL = i18n.language === 'nl';

  return (
    <>
      <SEOHead page="food-poisoning-report" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section with Keywords */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {isNL ? 'Voedselvergiftiging Melden' : 'Report Food Poisoning'}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-6">
              {isNL 
                ? 'Bent u ziek geworden na het eten in een restaurant? Meld het hier anoniem.'
                : 'Got sick after eating at a restaurant? Report it anonymously here.'}
            </p>
          </div>

          {/* Keywords Section for SEO */}
          <div className="mb-8 p-6 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
              {isNL ? 'Waarom Melden?' : 'Why Report?'}
            </h2>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li>• {isNL ? 'Help andere bezoekers voedselvergiftiging te voorkomen' : 'Help other visitors prevent food poisoning'}</li>
              <li>• {isNL ? 'Draag bij aan betere restaurant hygiëne' : 'Contribute to better restaurant hygiene'}</li>
              <li>• {isNL ? 'Volledig anoniem - geen persoonlijke gegevens vereist' : 'Completely anonymous - no personal data required'}</li>
              <li>• {isNL ? 'Sneller dan officiële melding procedures' : 'Faster than official reporting procedures'}</li>
            </ul>
          </div>

          {/* Common Symptoms Section */}
          <div className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
              {isNL ? 'Symptomen van Voedselvergiftiging' : 'Food Poisoning Symptoms'}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">
                  {isNL ? 'Veel Voorkomende Symptomen:' : 'Common Symptoms:'}
                </h3>
                <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                  <li>• {isNL ? 'Misselijkheid en braken' : 'Nausea and vomiting'}</li>
                  <li>• {isNL ? 'Diarree' : 'Diarrhea'}</li>
                  <li>• {isNL ? 'Buikpijn en krampen' : 'Stomach pain and cramps'}</li>
                  <li>• {isNL ? 'Koorts' : 'Fever'}</li>
                  <li>• {isNL ? 'Hoofdpijn' : 'Headache'}</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">
                  {isNL ? 'Wanneer Medische Hulp Zoeken:' : 'When to Seek Medical Help:'}
                </h3>
                <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                  <li>• {isNL ? 'Hoge koorts (>38.5°C)' : 'High fever (>38.5°C)'}</li>
                  <li>• {isNL ? 'Bloed in ontlasting' : 'Blood in stool'}</li>
                  <li>• {isNL ? 'Uitdrogingsverschijnselen' : 'Signs of dehydration'}</li>
                  <li>• {isNL ? 'Symptomen langer dan 3 dagen' : 'Symptoms lasting over 3 days'}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Community Platform Benefits */}
          <div className="mb-8 p-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
              {isNL ? 'Direct Melden aan de Gemeenschap' : 'Report Directly to the Community'}
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {isNL
                ? 'Meld direct en anoniem aan de gemeenschap zonder lange formulieren of wachttijden. Uw melding wordt direct zichtbaar voor andere bezoekers en helpt hen veiligere keuzes te maken.'
                : 'Report directly and anonymously to the community without long forms or waiting times. Your report becomes immediately visible to other visitors and helps them make safer choices.'}
            </p>
            <div className="flex space-x-4">
              <NavigationLink
                to="/how-to-check"
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                {isNL ? 'Meer over hoe het werkt' : 'Learn how it works'}
              </NavigationLink>
            </div>
          </div>

          {/* Report Form */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
              {isNL ? 'Maak Uw Melding' : 'Make Your Report'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {isNL
                ? 'Zoek het restaurant waar u ziek van bent geworden en deel uw ervaring.'
                : 'Search for the restaurant where you got sick and share your experience.'}
            </p>
            {/* The actual report form would go here */}
            <div className="text-center p-8 bg-white dark:bg-gray-700 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">
                {isNL 
                  ? 'Gebruik de zoekbalk bovenaan de pagina om het restaurant te vinden'
                  : 'Use the search bar at the top of the page to find the restaurant'}
              </p>
            </div>
          </div>

          {/* SEO Footer Content */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {isNL ? 'Veelgestelde Vragen over Voedselvergiftiging' : 'Food Poisoning FAQs'}
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-800 dark:text-gray-200">
                  {isNL ? 'Hoe lang duurt voedselvergiftiging?' : 'How long does food poisoning last?'}
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  {isNL
                    ? 'De meeste gevallen van voedselvergiftiging duren 1-3 dagen. Bij ernstige gevallen kan het langer duren.'
                    : 'Most cases of food poisoning last 1-3 days. Severe cases may last longer.'}
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 dark:text-gray-200">
                  {isNL ? 'Wat zijn de eerste symptomen?' : 'What are the first symptoms?'}
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  {isNL
                    ? 'Meestal beginnen symptomen 2-6 uur na het eten met misselijkheid, gevolgd door braken of diarree.'
                    : 'Symptoms usually start 2-6 hours after eating with nausea, followed by vomiting or diarrhea.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}